/**
 * Helper class for date and time operations
 */
export class DatesHelper {
  /**
   * Converte uma data no formato ISO para o formato DD/MM/YY - HH:MM:SS.
   * @param {string} isoDate - A data no formato ISO (yyyy-MM-ddTHH:mm:ss).
   * @returns {string} A data convertida no formato DD/MM/YY - HH:MM:SS.
   * @example DatesHelper.convertISOToBrazilianDateTimeFormat("2024-04-13T12:00:00") // "13/04/24 - 12:00:00"
   */
  static convertISOToBrazilianDateTimeFormat(isoDate) {
    if(isoDate){
      const [date, time] = isoDate.split('T');
      const [year, month, day] = date.split('-');
      const formattedTime = time.replace(/(\.\d{3}Z)$/, ''); // Remove the 000Z part if present
      return `${day}/${month}/${year} - ${formattedTime}`;
    }
      return '-'
  }

  /**
   * Retorna o horário no formato HH:MM:SS a partir de uma data fornecida.
   * @param {Date} date - A data no formato new Date.
   * @returns {string} O horário no formato HH:MM:SS.
   * @example DatesHelper.getTimeFromDate(new Date()) // "14:30:00"
   */
  static getTimeFromDate(date) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Converte uma data no formato new Date para o formato dd/MM/yyyy.
   * @param {Date} date - A data no formato new Date.
   * @returns {string} A data convertida no formato dd/MM/yyyy.
   * @example DatesHelper.convertDateObjectToBrazilianFormat(new Date()) // "13/04/2024"
   */
  static convertDateObjectToBrazilianFormat(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Converte uma data no formato new Date para o formato HH:mm:ss.
   * @param {Date} date - A data no formato new Date.
   * @returns {string} A hora convertida no formato HH:mm:ss.
   * @example DatesHelper.convertDateObjectToBrazilianTimeFormat(new Date()) // "14:30:00"
   */
  static convertDateObjectToBrazilianTimeFormat(date) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Converte a data fornecida no formato yyyy-MM-dd para o formato dd/MM/yyyy.
   * @param {string} date - A data no formato yyyy-MM-dd.
   * @returns {string} A data convertida no formato dd/MM/yyyy.
   * @example DatesHelper.convertToBrazilianFormat("2024-04-13") // "13/04/2024"
   */
  static convertToBrazilianFormat(date) {
    return date.split('-').reverse().join('/');
  }

  /**
   * Retorna a data de hoje no formato brasileiro, dd/MM/yyyy.
   *
   * @returns {string} A data de hoje no formato brasileiro.
   * @example DatesHelper.getTodayInBrazilianFormat() // "13/04/2024"
   */
  static getTodayInBrazilianFormat() {
    return new Date()
      .toISOString()
      .split('T')[0]
      .split('-')
      .reverse()
      .join('/');
  }

  /**
   * Retorna a data de hoje no formato ISO, yyyy-MM-dd.
   *
   * @returns {string} A data de hoje no formato ISO.
   * @example DatesHelper.getToday() // "2024-04-13"
   */
  static getToday() {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Remove os segundos de um horário.
   * @param {string} time - O horário.
   * @returns {string} O horário sem os segundos.
   * @example DatesHelper.removeSecondsFromTime("12:00:00") // "12:00"
   */
  static removeSecondsFromTime(time) {
    return time.split(':').slice(0, 2).join(':');
  }

  /**
   * Converte a data fornecida no formato dd/MM/yyyy para o formato yyyy-MM-dd.
   * @param {string} date - A data no formato dd/MM/yyyy.
   * @returns {string} A data convertida no formato yyyy-MM-dd.
   * @example DatesHelper.convertToISOFormat("13/04/2024") // "2024-04-13"
   */
  static convertToISOFormat(date) {
    return date.split('/').reverse().join('-');
  }

  /**
   * Compila a data fornecida no formato dd/MM/yyyy e o horário fornecido no formato HH:mm:ss em uma string de data e hora.
   * @param {string} date - A data no formato dd/MM/yyyy.
   * @param {string} time - A hora no formato HH:mm:ss.
   * @returns {string} A data e hora compiladas.
   * @example DatesHelper.compileDateAndTime("13/04/2024", "12:00:00") // "2024-04-13T12:00:00"
   */
  static compileDateAndTime(date, time) {
    return `${this.convertToISOFormat(date)}T${time}`;
  }

  /**
   * Compara quantas horas faltam para a data e hora fornecidas em relação à data e hora atuais.
   * @param {string} dateTime - A data e hora no formato yyyy-MM-ddTHH:mm:ss.
   * @returns {number} A diferença em horas.
   * @example DatesHelper.getHoursDifference("2024-04-13T12:00:00") // 24
   */
  static getHoursDifference(dateTime) {
    const dateTimeInMilliseconds = new Date(dateTime).getTime();
    const currentDateTimeInMilliseconds = new Date().getTime();

    return (
      (dateTimeInMilliseconds - currentDateTimeInMilliseconds) / (60000 * 60)
    );
  }

  /**
   * Compara quantos minutos faltam para a data e hora fornecidas em relação à data e hora atuais.
   * @param {string} dateTime - A data e hora no formato yyyy-MM-ddTHH:mm:ss.
   * @returns {number} A diferença em minutos.
   * @example DatesHelper.getMinutesDifference("2024-04-13T12:00:00") // 1440
   */
  static getMinutesDifference(dateTime) {
    const dateTimeInMilliseconds = new Date(dateTime).getTime();
    const currentDateTimeInMilliseconds = new Date().getTime();

    return (dateTimeInMilliseconds - currentDateTimeInMilliseconds) / 60000;
  }

  /**
   * Compara quantos dias faltam para a data fornecida em relação à data atual.
   * @param {string} date - A data no formato yyyy-MM-dd.
   * @returns {number} A diferença em dias.
   * @example DatesHelper.getDaysDifference("2024-04-13") // 24
   */
  static getDaysDifference(date) {
    const currentDate = new Date().toISOString().split('T')[0];
    return this.calculateDaysDifference(currentDate, date);
  }

  /**
   * Calcula a diferença em dias entre duas datas
   * @param {string} date1 A primeira data no formato yyyy-MM-dd
   * @param {string} date2 A segunda data no formato yyyy-MM-dd
   * @returns {number} A diferença em dias
   * @example DatesHelper.calculateDaysDifference("2024-04-13", "2024-04-14") // 1
   */
  static calculateDaysDifference(date1, date2) {
    const date1InMilliseconds = new Date(date1).getTime();
    const date2InMilliseconds = new Date(date2).getTime();

    return (date2InMilliseconds - date1InMilliseconds) / (60000 * 60 * 24);
  }

  /**
   * Retorna a data do dia anterior
   * @param {string} date A data de referência no formato yyyy-MM-dd
   * @returns {string} Data do dia anterior no formato yyyy-MM-dd
   * @example DatesHelper.getPreviousDate("2024-04-13") // "2024-04-12"
   */
  static getPreviousDate(date) {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    return currentDate.toISOString().split('T')[0];
  }

  /**
   * Retorna a data do dia posterior
   * @param {string} date A data de referência no formato yyyy-MM-dd
   * @returns {string} Data do dia posterior no formato yyyy-MM-dd
   * @example DatesHelper.getNextDate("2024-04-13") // "2024-04-14"
   */
  static getNextDate(date) {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + 1);
    return currentDate.toISOString().split('T')[0];
  }

  /**
   * Retorna a data base adicionada de n dias
   * @param {string} date A data de referência no formato yyyy-MM-dd
   * @param {number} daysToAdd - Quantos dias adicionar à data base
   * @returns {string} Data base adicionada de dias_para_adicionar no formato yyyy-MM-dd
   * @example DatesHelper.getDateAfterNDays("2024-04-13", 5) // "2024-04-18"
   */
  static getDateAfterNDays(date, daysToAdd) {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + daysToAdd);
    return currentDate.toISOString().split('T')[0];
  }

  /**
   * Retorna o mês anterior à data fornecida
   * @param {string} date A data de referência no formato yyyy-MM-dd
   * @returns {string} Data do mês anterior no formato yyyy-MM-dd
   * @example DatesHelper.getPreviousMonth("2024-04-13") // "2024-03-13"
   */
  static getPreviousMonth(date) {
    const currentDate = new Date(date);
    currentDate.setDate(0);
    return currentDate.toISOString().split('T')[0];
  }

  /**
   * Retorna o mês posterior à data fornecida
   * @param {string} date A data de referência no formato yyyy-MM-dd
   * @returns {string} Data do mês posterior no formato yyyy-MM-dd
   * @example DatesHelper.getNextMonth("2024-04-13") // "2024-05-13"
   */
  static getNextMonth(date) {
    const currentDate = new Date(date);
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 2,
      1
    );
    return nextMonth.toISOString().split('T')[0];
  }

  /**
   * Aplica uma quantidade de horas ao horário fornecido e retorna o novo horário.
   * @param {string} time O horário de referência no formato hh:mm:ss
   * @param {number} hours A quantidade de horas a serem aplicadas
   * @returns {string} O novo horário no formato hh:mm:ss
   * @example DatesHelper.applyHours("12:00:00", 2) // "14:00:00"
   */
  static applyHours(time, hours) {
    const [hh, mm, ss] = time.split(':').map(Number);
    const currentDate = new Date();
    currentDate.setHours(hh, mm, ss);
    currentDate.setHours(currentDate.getHours() + hours);
    return currentDate.toTimeString().split(' ')[0];
  }

  /**
   * Aplica uma quantidade de horas a uma data ISO e retorna a nova data ISO.
   * @param {string} isoDateTime A data ISO no formato yyyy-MM-ddThh:mm:ss
   * @param {number} hours A quantidade de horas a serem aplicadas
   * @returns {string} A nova data ISO no formato yyyy-MM-ddThh:mm:ss
   * @example DatesHelper.applyHoursToISO("2024-04-13T12:00:00", 2) // "2024-04-13T14:00:00"
   */
  static applyHoursToISO(isoDateTime, hours) {
    const date = new Date(isoDateTime);
    date.setHours(date.getHours() + hours);
    return date.toISOString().replace(/\.\d{3}Z$/, '');
  }

  /**
   * Aplica uma quantidade de horas ao horário fornecido e retorna o novo horário em formato brasileiro.
   * @param {string} time O horário de referência no formato hh:mm:ss
   * @param {number} hours A quantidade de horas a serem aplicadas
   * @returns {string} O novo horário no formato hh:mm:ss
   * @example DatesHelper.applyHoursInBrazilianFormat("12:00:00", 2) // "14:00:00"
   */
  static applyHoursInBrazilianFormat(time, hours) {
    return this.applyHours(time, hours);
  }
}
